import { defineStore } from 'pinia';

import '~/angular/app/pinia';

/**
 * The viewer - who is looking at the page - as one reactive object.
 *
 * `CurrentUser` used to read `window.$user` straight through, which made it a snapshot of
 * what django rendered rather than a source of truth: a computed property reading it never
 * re-evaluated, and the handful of things that do change while a page is open (a name, an
 * avatar, the searchable switch) either wrote to a local copy nobody else saw or had no
 * setter at all. Store state is reactive, so every one of the ~240 `new CurrentUserClass()`
 * call sites re-renders when the answer changes, without any of them being touched.
 *
 * Every key is declared in `defaultState` even when the server omits it, so an accessor
 * reads the absence as the falsy value it means rather than as `undefined`: `ah_link` is
 * rendered only for portal admins, `rba_tables_link` only for RBA table admins, `wiki_id`
 * only for wiki users, and for an anonymous visitor the blob holds nothing but
 * `is_authenticated`. A key that arrives later - which is what fetching these over the api
 * will do - is reactive whether it was declared or not.
 */
function defaultState () {
    return {
        is_authenticated: false,

        // identity
        userprofile_id: null,
        username: '',
        profile_link: '',
        first_name: '',
        last_name: '',
        email: '',
        avatar: '',

        // what the viewer is
        is_superuser: false,
        is_ah: false,
        is_buddy: false,
        is_circle_user: false,
        is_searchable: false,
        can_create_diary_proxy_entries: false,

        // portal
        ah_id: null,
        ah_link: null,
        ah_terms: {},

        // role based access
        is_rba_scope_admin: false,
        is_rba_insights_supervisor: false,
        rba_tables_link: null,

        // the viewer's own stuff
        blog_id: null,
        wiki_enabled: false,
        wiki_id: null,
        dashboard_config: {},
        permissions: {},

        // account state
        pending_to_delete: false,

        // the popup queue in gdpr_handler - see GDPRPopupService
        is_policy_signed: false,
        visibility_changes_signed: false,
        is_my_story_empty: false,
        is_my_media_empty: false,
        password_marked_as_weak: false,
        weak_password_reminder_postponed: false,
        first_and_last_name_set_postponed: false,
        fill_profile_story_postponed: false,
        add_avatar_postponed: false,
    };
}

const useViewerStore = defineStore('viewer', {
    // seeded from the blob templates/base.html renders. That is the only reader of
    // `window.$user` left, so pointing it at an api response later is a change to this line.
    state: () => Object.assign(defaultState(), window.$user || {}),

    actions: {
        /** Merge a batch of fields - the initial seed, or a later fetch. */
        hydrate (data) {
            this.$patch(data || {});
        },

        /**
         * Back to the shape a page starts from. For tests.
         *
         * Not `$reset()`: that re-runs `state()`, which would seed `window.$user` back in.
         */
        reset () {
            this.$patch(defaultState());
        },
    },
});

export default useViewerStore;
